/**
 * Meeting Module Permission Hooks
 * Following Collection architecture for consistency
 * Light QA Contract Compliant
 */

import { useAuth } from '@/hooks/useAuth';
import { useAuthQuery } from '@/hooks/useAuthQuery';

// Enhanced permission hook with project-aware caching (same as Collection)
// Optimized: Increased staleTime and disabled refetchOnWindowFocus to prevent CORS/connection errors
const useOptimizedPermissionCheck = (permissionCode: string, projectId?: string) => {
  const { user } = useAuth();
  
  return useAuthQuery<boolean>({
    queryKey: ['meeting-permissions', user?.id, projectId, permissionCode],
    queryFn: async () => {
      const { checkUserPermission } = await import('@/lib/queries/authQueries');
      return checkUserPermission(user?.id || '', permissionCode);
    },
    requiresAuth: true,
    requiresSession: true,
    enabled: !!permissionCode && !!user?.id,
    staleTime: 60 * 1000, // 1 minute - prevents excessive RPC calls
    gcTime: 2 * 60 * 1000, // 2 minutes
    refetchOnMount: true,
    refetchOnWindowFocus: false, // Disabled to prevent connection overload
  });
};

// Base access check - Tab visibility
export const useMeetingAccess = (projectId?: string) => {
  return useOptimizedPermissionCheck('MEETING_ACCESS', projectId);
};

// View permissions
export const useMeetingViewPermissions = (projectId?: string) => {
  return useOptimizedPermissionCheck('MEETING_VIEW', projectId);
};

// CRUD permissions
export const useMeetingCRUDPermissions = (projectId?: string) => {
  const createMeeting = useOptimizedPermissionCheck('MEETING_CREATE', projectId);
  const editMeeting = useOptimizedPermissionCheck('MEETING_EDIT', projectId);
  const deleteMeeting = useOptimizedPermissionCheck('MEETING_DELETE', projectId);

  return {
    canCreate: createMeeting.data ?? false,
    canEdit: editMeeting.data ?? false,
    canDelete: deleteMeeting.data ?? false,
    isLoading: createMeeting.isLoading || editMeeting.isLoading || deleteMeeting.isLoading,
  };
};

// Attendee management permissions
export const useMeetingAttendeePermissions = (projectId?: string) => {
  return useOptimizedPermissionCheck('MEETING_MANAGE_ATTENDEES', projectId);
};

// Action item management permissions
export const useMeetingActionItemPermissions = (projectId?: string) => {
  return useOptimizedPermissionCheck('MEETING_MANAGE_ACTION_ITEMS', projectId);
};

// Export permissions
export const useMeetingExportPermissions = (projectId?: string) => {
  return useOptimizedPermissionCheck('MEETING_EXPORT', projectId);
};

// UI-ready permission hook (composite)
export const useMeetingUIPermissions = (projectId: string) => {
  const crudPerms = useMeetingCRUDPermissions(projectId);
  const attendeePerms = useMeetingAttendeePermissions(projectId);
  const actionItemPerms = useMeetingActionItemPermissions(projectId);
  const exportPerms = useMeetingExportPermissions(projectId);

  const isLoading = crudPerms.isLoading || attendeePerms.isLoading || 
                    actionItemPerms.isLoading || exportPerms.isLoading;

  return {
    // CRUD permissions
    canCreate: crudPerms.canCreate,
    canEdit: crudPerms.canEdit,
    canDelete: crudPerms.canDelete,

    // Feature permissions
    canManageAttendees: attendeePerms.data ?? false,
    canManageActionItems: actionItemPerms.data ?? false,
    canExport: exportPerms.data ?? false,

    // Loading state
    isLoading,

    // UI Helper: Get props for disabled elements
    getDisabledProps: (hasPermission: boolean, additionalDisabled = false) => ({
      disabled: !hasPermission || additionalDisabled || isLoading,
      className: !hasPermission || additionalDisabled ? 'opacity-50 cursor-not-allowed' : ''
    }),

    // UI Helper: Get tooltip text for disabled states
    getTooltipText: (hasPermission: boolean, action: string) => {
      if (isLoading) return 'Đang kiểm tra quyền...';
      if (!hasPermission) return `Bạn không có quyền ${action}`;
      return '';
    }
  };
};
